import React, { useState } from "react";
import { TextField, MenuItem, Alert } from "@mui/material";
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PopupBase from "../../../popup/base/PopupBase";
import CustomButton from "../../../components/buttons/CustomButton";
import { DataProviderInstance } from '../../../api/DataProvider';
import SourceAvatar from '../shared/SourceAvatar';
import CreateEditSourcePopup from '../sources/CreateEditSourcePopup';
import shared from '../shared/MarketingShared.module.css';

// Valor da opção "Cadastrar origem" no select (nunca é gravado na campanha)
const NEW_SOURCE_OPTION = "__new_source__";

function Field({ id, label, required = false, children }) {
  return (
    <div className={shared.field}>
      <label className={shared.label} htmlFor={id}>
        {label}{required && <span className={shared.required}>*</span>}
      </label>
      {children}
    </div>
  );
}

function CreateEditCampaignPopup(props) {
  const { Data, OnHide, OnUpdate } = props;

  const { CompanyConfig, SetCampaign, UpdateCampaign } = DataProviderInstance();

  const [campaignData, setCampaignData] = useState({
    id: Data?.id || null,
    name: Data?.name || "",
    sourceId: Data?.sourceId || "",
    defaultMessage: Data?.defaultMessage || "",
    description: Data?.description || "",
  });

  const [errors, setErrors] = useState({ name: false, sourceId: false });
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [sourcePopupVisible, setSourcePopupVisible] = useState(false);

  // The arrays are guaranteed to be initialized in DataProvider.js
  const sourceOptions = CompanyConfig.LeadsSources.map((item) => ({
    id: item.id,
    name: item.name,
  }));

  const handleChange = (field, value) => {
    setCampaignData({ ...campaignData, [field]: value });
    setErrors((previous) => ({ ...previous, [field]: false }));
    setSaveError("");
  };

  // Origem criada pelo atalho do campo: já fica escolhida na campanha
  const onSourceCreated = (response) => {
    if (!response?.source_id) return;
    // Texto, como os ids que a lista de origens traz do servidor
    setCampaignData((previous) => ({ ...previous, sourceId: String(response.source_id) }));
    setErrors((previous) => ({ ...previous, sourceId: false }));
  };

  const onSourceSelect = (value) => {
    if (value === NEW_SOURCE_OPTION) {
      setSourcePopupVisible(true);
      return;
    }
    handleChange("sourceId", value);
  };

  // Só fecha quando o servidor confirma; senão o erro aparece no popup
  const onSaveResponse = (response) => {
    setSaving(false);
    if (response?.status == "success") {
      OnUpdate(response, Boolean(Data));
      OnHide();
      return;
    }
    setSaveError(response?.message || response?.error || "Não foi possível salvar a campanha. Tente novamente.");
  };

  const createCampaign = () => {
    setSaving(true);
    SetCampaign(campaignData, onSaveResponse);
  };

  const updateData = () => {
    setSaving(true);
    UpdateCampaign(campaignData, onSaveResponse);
  };

  const validateForm = () => {
    const nextErrors = {
      name: !String(campaignData.name || "").trim(),
      sourceId: !campaignData.sourceId,
    };
    setErrors(nextErrors);
    return !nextErrors.name && !nextErrors.sourceId;
  };

  const saveClickHandler = () => {
    if (!validateForm()) return;

    if (Data) {
      updateData();
    } else {
      createCampaign();
    }
  };

  const footer = (
    <div className={shared.footer}>
      <CustomButton label="Cancelar" variant="text" onClick={OnHide} />
      <CustomButton
        variant="contained"
        style="main"
        label={Data ? "Salvar alterações" : "Adicionar"}
        onClick={saveClickHandler}
        enabled={!saving}
      />
    </div>
  );

  const content = (
    <div className={shared.form}>
      {saveError && <Alert severity="error">{saveError}</Alert>}

      <Field id="campaign-name" label="Nome da campanha" required>
        <TextField
          id="campaign-name"
          fullWidth
          size="small"
          placeholder="Ex.: Promoção de inverno"
          value={campaignData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          required
          error={errors.name}
          helperText={errors.name ? "Preencha o nome da campanha" : ""}
          autoFocus
          className={shared.input}
        />
      </Field>

      <Field id="campaign-source" label="Origem" required>
        <TextField
          id="campaign-source"
          select
          fullWidth
          size="small"
          value={campaignData.sourceId}
          onChange={(e) => onSourceSelect(e.target.value)}
          required
          error={errors.sourceId}
          helperText={errors.sourceId ? "Escolha a origem da campanha" : ""}
          className={shared.input}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => {
              const selected = sourceOptions.find((s) => s.id == value);
              if (!selected) return <span style={{ color: 'var(--color-text-light)' }}>Escolha a origem</span>;
              return (
                <span className={shared.option}>
                  <SourceAvatar name={selected.name} size="sm" />{selected.name}
                </span>
              );
            },
          }}
        >
          {sourceOptions.length === 0 && (
            <MenuItem value="" disabled>Nenhuma origem cadastrada</MenuItem>
          )}
          {sourceOptions.map((source) => (
            <MenuItem key={source.id} value={source.id}>
              <span className={shared.option}>
                <SourceAvatar name={source.name} size="sm" />{source.name}
              </span>
            </MenuItem>
          ))}
          <MenuItem value={NEW_SOURCE_OPTION} className={sourceOptions.length > 0 ? shared.optionActionItem : undefined}>
            <span className={`${shared.option} ${shared.optionAction}`}>
              <AddRoundedIcon style={{ fontSize: 18 }} />Cadastrar origem
            </span>
          </MenuItem>
        </TextField>
      </Field>

      <Field id="campaign-message" label="Mensagem padrão">
        <TextField
          id="campaign-message"
          fullWidth
          multiline
          minRows={3}
          placeholder="Ex.: Olá! Vi a promoção de inverno e quero saber mais."
          value={campaignData.defaultMessage}
          onChange={(e) => handleChange("defaultMessage", e.target.value)}
          helperText="Identifica os leads que chegam por esta campanha."
          className={shared.input}
        />
      </Field>

      <Field id="campaign-description" label="Descrição">
        <TextField
          id="campaign-description"
          fullWidth
          multiline
          minRows={2}
          placeholder="Para você lembrar do que se trata"
          value={campaignData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          className={shared.input}
        />
      </Field>
    </div>
  );

  return (
    <>
      <PopupBase
        Title={Data ? "Editar campanha" : "Nova campanha"}
        Content={content}
        Footer={footer}
        OnClose={OnHide}
        maxWidth="sm"
      />
      {sourcePopupVisible && (
        <CreateEditSourcePopup
          OnHide={() => setSourcePopupVisible(false)}
          OnUpdate={onSourceCreated}
        />
      )}
    </>
  );
}

export default CreateEditCampaignPopup;
